// Story 7.1 — TanStack Query mutation for POST /api/v1/auth/login.
//
// FIRST SPA consumer of the login endpoint (Story 1.11). Successful
// login sets an httpOnly session cookie server-side; SPA reads
// success via the response envelope and invalidates ["account-me"].
//
// Backend behavior (verified against server/src/routes/auth.rs +
// server/src/error.rs):
//   * 200 → LoginResponse { user_id, session_id, is_admin,
//                           requires_2fa_step }.
//     There is NO email_verified field on 200 — the backend gates
//     login on it and returns 401 EMAIL_NOT_VERIFIED for unverified.
//   * 401 code: "INVALID_CREDENTIALS" → wrong pw or unknown email.
//   * 401 code: "EMAIL_NOT_VERIFIED" → account exists but not verified;
//     backend response envelope includes `resend_url`.
//   * 429 → rate limited.

import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export interface SignInVariables {
  email: string;
  password: string;
}

export interface SignInResponseData {
  user_id: string;
  session_id: string;
  is_admin: boolean;
  /** TRUE = user has TOTP enrolled; the session is partial until the SPA
   *  collects a code on /2fa/verify (POST /auth/2fa-verify). */
  requires_2fa_step: boolean;
}

export type SignInErrorCode =
  | "INVALID_CREDENTIALS"
  | "EMAIL_NOT_VERIFIED"
  | "RATE_LIMITED"
  | "VALIDATION_FAILED"
  | "SIGN_IN_FAILED";

export interface SignInError {
  code: SignInErrorCode | string;
  message: string;
  field?: string;
  status: number;
}

function freshIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function postSignIn(
  variables: SignInVariables,
): Promise<SignInResponseData> {
  const response = await fetch("/api/v1/auth/login", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": freshIdempotencyKey(),
    },
    body: JSON.stringify({
      email: variables.email,
      password: variables.password,
    }),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: SignInError = {
      code: "SIGN_IN_FAILED",
      message: `Sign-in failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          field: raw.error.field,
          status: response.status,
        };
      }
    } catch {
      // Body not JSON — keep default.
    }
    throw detail;
  }
  return (await response.json()) as SignInResponseData;
}

export function useSignIn(): UseMutationResult<
  SignInResponseData,
  SignInError,
  SignInVariables
> {
  const queryClient = useQueryClient();
  return useMutation<SignInResponseData, SignInError, SignInVariables>({
    mutationFn: postSignIn,
    retry: false,
    onSuccess: () => {
      // SECURITY: wipe every cached query from any PRIOR session before the
      // newly-authenticated user's pages mount, so they never read the
      // previous user's user-scoped data (application status/draft, wallet,
      // collection…). Defense-in-depth alongside the logout-time clear —
      // covers sessions that ended without an explicit logout (expiry, a
      // different user logging in on the same tab). The sign-in page holds
      // no user-scoped observers, so nothing refetches here; the new user's
      // pages fetch fresh on navigation.
      queryClient.clear();
    },
  });
}
